import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Phone, Users, ShoppingBag, FileText, Settings, LogOut,
  Sun, Moon, Menu, X, ChevronRight, BarChart2, Smartphone, Wrench,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { useFontSize } from '../../contexts/FontSizeContext';

const navItems = [
  { to: '/calls',          label: 'שיחות נכנסות', icon: Phone },
  { to: '/customers',      label: 'לקוחות',        icon: Users },
  { to: '/orders',         label: 'הזמנות',         icon: ShoppingBag },
  { to: '/service-calls',  label: 'קריאות שירות',  icon: Wrench },
  { to: '/field',          label: 'מצב שטח',        icon: Smartphone },
  { to: '/sales',          label: 'מכירות',         icon: BarChart2 },
  { to: '/documents',      label: 'מסמכים',         icon: FileText },
  { to: '/settings',       label: 'הגדרות',         icon: Settings },
];

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const { dark, toggle } = useDarkMode();
  const { decrease, increase, canDecrease, canIncrease } = useFontSize();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768);

  function handleLogout() {
    logout();
    navigate('/login');
  }

  const visibleItems = isAdmin ? navItems : navItems.filter((i) => i.to !== '/settings');

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900" dir="rtl">
      {/* Sidebar */}
      <aside
        className={`
          flex flex-col bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700
          transition-all duration-200 shrink-0
          ${sidebarOpen ? 'w-56' : 'w-14'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 h-14">
          {sidebarOpen && (
            <span className="font-bold text-primary-600 dark:text-primary-400 text-lg truncate">
              Riteback
            </span>
          )}
          <button
            onClick={() => setSidebarOpen((o) => !o)}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ml-auto"
          >
            {sidebarOpen ? <ChevronRight size={18} /> : <Menu size={18} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 space-y-0.5 px-2 overflow-y-auto">
          {visibleItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm font-medium transition-colors
                 ${isActive
                   ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                   : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                 }`
              }
              title={!sidebarOpen ? label : undefined}
            >
              <Icon size={18} className="shrink-0" />
              {sidebarOpen && <span className="truncate">{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-2 space-y-0.5">
          {/* Font size controls */}
          <div
            className="flex items-center gap-1 px-2.5 py-1.5"
            title={!sidebarOpen ? 'גודל טקסט בטבלאות' : undefined}
          >
            <button
              onClick={decrease}
              disabled={!canDecrease}
              className="flex items-center justify-center w-7 h-7 rounded-md text-gray-600 dark:text-gray-400
                         hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed
                         transition-colors text-xs font-bold select-none"
              title="הקטן טקסט"
            >
              A-
            </button>
            {sidebarOpen && (
              <span className="flex-1 text-xs text-gray-400 dark:text-gray-500 text-center select-none">
                גודל טקסט
              </span>
            )}
            <button
              onClick={increase}
              disabled={!canIncrease}
              className="flex items-center justify-center w-7 h-7 rounded-md text-gray-600 dark:text-gray-400
                         hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed
                         transition-colors text-sm font-bold select-none"
              title="הגדל טקסט"
            >
              A+
            </button>
          </div>

          <button
            onClick={toggle}
            className="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400
                       hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title={!sidebarOpen ? (dark ? 'מצב יום' : 'מצב לילה') : undefined}
          >
            {dark ? <Sun size={18} className="shrink-0" /> : <Moon size={18} className="shrink-0" />}
            {sidebarOpen && <span>{dark ? 'מצב יום' : 'מצב לילה'}</span>}
          </button>

          {sidebarOpen && (
            <div className="px-2.5 py-1.5">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{user?.name}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 truncate" dir="ltr">{user?.email}</p>
            </div>
          )}

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-red-600 dark:text-red-400
                       hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title={!sidebarOpen ? 'יציאה' : undefined}
          >
            <LogOut size={18} className="shrink-0" />
            {sidebarOpen && <span>יציאה</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
